import { FC, PropsWithChildren, useCallback, useRef, useState } from "react";
import { Box, useBreakpoints } from "@otrium/core";
import SwiperClass from "swiper/types/swiper-class";
import { SwiperSlide } from "swiper/react";
import { useFocusFirstFocusable } from "src/hooks/useFocusFirstFocusable";
import { ChevronDown } from "src/icons/ChevronDown";
import {
  StyledProductPhotos,
  StyledSwiper,
  StyledButtonBack,
  StyledButtonNext,
  StyledDotsPagination,
} from "./ProductPhotos.styled";
import LazyImage from "src/molecules/Lazyimage/Lazyimage";
import { LazyImageProvider } from "src/molecules/Lazyimage/LazyImageContext";
import { isSafari } from "src/utils/isSafari";
import { ProductThumbnails } from "src/molecules/ProductThumbnails";
import { ProductLabel } from "src/types/graphql.d";
import { ProductLabels } from "src/molecules/ProductLabels";
import { MAX_THUMBNAILS_PER_VIEW } from "src/config";
import NoSSR from "src/atoms/NoSSR";

interface Props extends PropsWithChildren {
  className?: string;
  photos: string[];
  currentSlide?: number;
  handleOnClick?: (index: number) => void;
  hasMasterSpinner?: boolean;
  autoFocus?: boolean;
  labels?: ProductLabel[];
  isInModal?: boolean;
  isClient?: boolean;
}

const WishlistButton = (props: any) => (
  <Box
    sx={{ position: "absolute", right: 0, zIndex: 2, top: 0 }}
    textAlign="right"
    {...props}
  />
);

const ProductPhotos: FC<Props> & {
  WishlistButton: typeof WishlistButton;
} = ({
  className,
  photos,
  currentSlide,
  handleOnClick,
  hasMasterSpinner = false,
  autoFocus = false,
  labels,
  isInModal = false,
  children,
  isClient,
  ...rest
}) => {
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const { isMobile } = useBreakpoints();

  const sliderRef = useRef<HTMLDivElement>(null);

  useFocusFirstFocusable(sliderRef, autoFocus);

  // ProductPhotos renders at max 8 photos
  const renderedPhotos = photos.slice(0, 8);

  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLElement>) => {
      event.preventDefault();

      if (swiper && handleOnClick) {
        handleOnClick(swiper.activeIndex);
      }
    },
    [handleOnClick, swiper]
  );

  // in case the swiper has a single image
  // dots and infinite options are set to false
  const swiperSingleImageProps = {
    pagination:
      renderedPhotos.length === 1
        ? false
        : isInModal
        ? { clickable: true }
        : { clickable: true, el: "#swiper-dots-pagination" },
  };

  // Slide up the thumbnail slider if the last thumbnail is clicked
  const handleLastThumbnailClick = (swiperInstance: SwiperClass) => {
    if (
      swiperInstance.thumbs.swiper &&
      swiperInstance.activeIndex - swiperInstance.thumbs.swiper.activeIndex ===
        MAX_THUMBNAILS_PER_VIEW - 1
    ) {
      swiperInstance.thumbs.swiper.slideNext();
    }
  };

  // Slide down the thumbnail slider if the first thumbnail is clicked
  const handleFirstThumbnailClick = (swiperInstance: SwiperClass) => {
    if (
      swiperInstance.thumbs.swiper &&
      swiperInstance.activeIndex === swiperInstance.thumbs.swiper.activeIndex
    ) {
      swiperInstance.thumbs.swiper.slidePrev();
    }
  };

  return (
    <>
      {!isInModal && (
        <ProductThumbnails
          thumbsSwiper={thumbsSwiper}
          setThumbsSwiper={setThumbsSwiper}
          photos={renderedPhotos}
          activeSlide={activeSlide}
          tabIndex={0}
          aria-label="Image thumbnails"
          isclient={isClient}
          role="listbox"
        />
      )}

      <LazyImageProvider>
        <StyledProductPhotos
          ref={sliderRef}
          isInModal={isInModal}
          className={className}
          {...rest}
        >
          <StyledSwiper
            slidesPerView={isMobile && !isInModal ? 1.6 : 1}
            cssMode={isSafari()} // NOTE: Don't forget to use "smoothscroll-polyfill" package to fix animation on Safari. Check Swiper API
            mousewheel={true}
            initialSlide={currentSlide}
            spaceBetween={8}
            navigation={{
              prevEl: "#swiper__prev-button",
              nextEl: "#swiper__next-button",
            }}
            keyboard={{ enabled: autoFocus }}
            onSwiper={setSwiper}
            thumbs={{ swiper: thumbsSwiper }}
            onActiveIndexChange={(swiperInstance) => {
              setActiveSlide(swiperInstance.activeIndex);
              handleFirstThumbnailClick(swiperInstance);
              handleLastThumbnailClick(swiperInstance);
            }}
            tabIndex={0}
            aria-label="Product image"
            role="listbox"
            isclient={isClient ? "true" : undefined}
            {...swiperSingleImageProps}
          >
            {renderedPhotos?.map((photo, index) => (
              <SwiperSlide
                key={index}
                onClick={onClickHandler}
                role="option"
                tabIndex={0}
                aria-current={index === activeSlide}
              >
                <LazyImage
                  aspectRatio={[2, 3]}
                  src={photo}
                  index={index}
                  isClient={isClient}
                />
              </SwiperSlide>
            ))}
            <Box
              sx={{
                position: "relative",
                top: "-38px",
                left: "16px",
                zIndex: 1,
              }}
            >
              {labels && <ProductLabels labels={labels.slice(0, 2)} />}
            </Box>
          </StyledSwiper>

          {renderedPhotos.length > 1 && !(isMobile && !isInModal) && (
            <NoSSR>
              <StyledButtonBack
                id="swiper__prev-button"
                aria-label="Previous photos"
                tabIndex={0}
              >
                <ChevronDown />
              </StyledButtonBack>
              <StyledButtonNext
                id="swiper__next-button"
                aria-label="Next photos"
                tabIndex={0}
              >
                <ChevronDown />
              </StyledButtonNext>
              <StyledDotsPagination id="swiper-dots-pagination" />
            </NoSSR>
          )}
          {children}
        </StyledProductPhotos>
      </LazyImageProvider>
    </>
  );
};

ProductPhotos.WishlistButton = WishlistButton;

export default ProductPhotos;
